/**
 * The rows out of a response that may or may not be paginated (KIN-1896).
 *
 * The same resource is served in two shapes. `patientletter/getbyconsultationid` returns a
 * bare `List<PatientLetterDTO>`; `patientletter/getbypatientid` returns
 * `PaginationResponse<PatientLetterDTO>` — `{ data, totalRecords, page, rowsPerPage }`.
 * Callers that take whichever one they happen to be given get an object where they expected
 * an array, and `?? []` does not catch it: an envelope is neither null nor undefined, so it
 * passes the guard and fails at the first array method.
 *
 * That is what broke the Letters tab on the pregnancy screens — `letters.at is not a
 * function` — and, more quietly, what made a read-only Letters tab render as empty rather
 * than throw, because `.length` on an object is `undefined`.
 *
 * Deliberately tolerant of both shapes rather than assuming one. A caller reading rows
 * should not have to know which endpoint answered, and the pair above shows that knowledge
 * going stale is exactly the failure mode.
 *
 * This returns only the rows. Where the count and page matter — a screen with paging
 * controls — use `toTotalRecords` alongside it.
 */
export const toRows = (response) => {
  if (Array.isArray(response)) return response;

  if (Array.isArray(response?.data)) return response.data;

  return [];
};

/**
 * How many rows exist in total behind a paginated response, or undefined when the
 * response isn't paginated and the caller already holds every row.
 *
 * `PaginationResponse` calls the field `totalRecords`. Several screens read
 * `totalCount` instead, which is always undefined — so the pager fell back to
 * counting the rows on screen and a patient with 13 letters was told "1-10 of 10",
 * with no way to reach the other three. `totalCount` and `total` are kept in the
 * chain because some older endpoints in this app do answer with those names, and
 * reading a field that isn't there is what caused this in the first place.
 */
export const toTotalRecords = (response) => {
  if (Array.isArray(response) || !response) return undefined;

  return response.totalRecords ?? response.totalCount ?? response.total;
};
